import { useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useSession } from '../session/useSession'
import { useI18n } from '../i18n/LocaleContext'
import { LanguageSwitch } from '../ui/LanguageSwitch'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Field } from '../ui/Field'
import { Logo, LogoMark } from '../ui/Logo'
import { EmailIcon, GoogleIcon } from '../ui/icons'
import { LegalNotice } from '../ui/LegalNotice'

/**
 * The auth hub. Both sign-in methods land back here (`/login`) — Supabase
 * resolves the session from the URL fragment, the session query updates, and
 * the redirect at the top of this component forwards the user on.
 *
 * `/login` rather than `/` as the redirect target on purpose: a bare origin
 * hits a glob edge case in Supabase's redirect allowlist and falls back to the
 * project's configured Site URL, which breaks local development.
 */
export function Login() {
  const session = useSession()
  const { t, fmt } = useI18n()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null)

  if (session.status === 'authenticated') return <Navigate to="/" replace />
  if (session.status === 'unprovisioned' || session.status === 'no_subscription') {
    return <Navigate to="/paywall" replace />
  }

  const redirectTo = `${window.location.origin}/login`

  // Supabase's messages are English; map the code and fall back to a generic line.
  function authErrorMessage(code: string | undefined): string {
    const errors = t.auth.errors as Record<string, string>
    return errors[code ?? ''] ?? errors.default ?? t.common.error
  }

  async function signInWithGoogle() {
    setError(null)
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    })
    if (oauthError) setError(authErrorMessage(oauthError.code))
  }

  async function sendMagicLink(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        // Signup happens through Stripe checkout, never here. Without this, a
        // stranger's address would silently create an orphan auth user.
        shouldCreateUser: false,
      },
    })
    setSubmitting(false)
    if (otpError) {
      setError(authErrorMessage(otpError.code))
      return
    }
    setLinkSentTo(email)
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-4 sm:p-6">
      <div className="grid w-full min-w-0 max-w-5xl overflow-hidden rounded-2xl bg-surface shadow-float lg:grid-cols-2">
        <AuthHero />

        <div className="flex min-w-0 flex-col justify-center px-7 py-12 sm:px-12">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-8 flex items-center justify-between">
              <span className="lg:hidden">
                <Logo size={28} />
              </span>
              <LanguageSwitch className="ml-auto" />
            </div>

            <h1 className="font-display text-[34px] font-normal tracking-tightish text-gray-900">
              {t.auth.signIn}
            </h1>
            <p className="mt-1.5 text-[13px] text-gray-500">{t.auth.signInSubtitle}</p>

            {linkSentTo ? (
              <div className="mt-8 space-y-5">
                <div className="flex items-start gap-3 rounded-md bg-sage-50 p-4">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-100 text-sage-700">
                    <EmailIcon size={16} />
                  </span>
                  <p className="text-[13px] leading-relaxed text-gray-700" role="status">
                    {fmt(t.auth.magicLinkSent, { email: linkSentTo })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setLinkSentTo(null)}
                  className="text-[13px] text-gray-500 transition-colors hover:text-gray-900"
                >
                  {t.auth.backToSignIn}
                </button>
              </div>
            ) : (
              <div className="mt-8 space-y-5">
                <form onSubmit={sendMagicLink} className="space-y-4">
                  <Field id="email" label={t.auth.email} required>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder={t.auth.emailPlaceholder}
                      className="h-11"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Field>
                  {error && (
                    <p className="text-[12.5px] text-red-700" role="alert">
                      {error}
                    </p>
                  )}
                  <Button type="submit" size="lg" disabled={submitting} className="w-full">
                    {t.auth.sendMagicLink}
                  </Button>
                </form>

                <div className="flex items-center gap-3">
                  <hr className="flex-1 border-DEFAULT" />
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t.auth.orSeparator}
                  </span>
                  <hr className="flex-1 border-DEFAULT" />
                </div>

                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => void signInWithGoogle()}
                  className="w-full gap-2.5"
                >
                  <GoogleIcon />
                  {t.auth.signInWithGoogle}
                </Button>

                <p className="text-[12.5px] text-gray-500">
                  {t.auth.noAccount}{' '}
                  <Link
                    to="/pricing"
                    className="font-semibold text-brand-700 underline-offset-[3px] hover:underline"
                  >
                    {t.auth.viewPlans}
                  </Link>
                </p>
                <LegalNotice />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

function AuthHero() {
  const { t } = useI18n()
  return (
    <div className="relative hidden overflow-hidden bg-ink lg:block" style={{ minHeight: 560 }}>
      {/* Layered radial washes give the panel depth without shipping an image. */}
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 15% 0%, rgb(var(--c-sage-400) / 0.45) 0%, rgb(var(--c-ink) / 0) 60%), radial-gradient(90% 80% at 100% 100%, rgb(var(--c-brand-500) / 0.45) 0%, rgb(var(--c-ink) / 0) 70%)',
        }}
      />
      <div className="relative flex h-full flex-col justify-between p-10 xl:p-12">
        <div className="flex items-center gap-2.5">
          <LogoMark size={40} />
          <span className="font-display text-[21px] text-gray-50">{t.brand.name}</span>
        </div>
        <div>
          <h2 className="max-w-[17ch] font-display text-[36px] font-normal leading-[1.12] tracking-tightish text-gray-50">
            {t.auth.heroTitle}
          </h2>
          <p className="mt-5 max-w-[42ch] text-[15px] leading-relaxed text-gray-300">
            {t.auth.heroSubline}
          </p>
        </div>
      </div>
    </div>
  )
}
